import React, { useState, useEffect } from 'react';
import adminApi from '../adminApi';
import { useLanguage } from '../context/LanguageContext';
import './Backups.css';

// ---- Types ----
interface Backup {
  id: number;
  file_path: string;
  database_type: string;
  size_bytes: number;
  created_at: string;
  created_by: string;
}

// ---- Helper Components (Icons) ----
const ClockIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>;
const Spinner = () => <div className="spinner"></div>;

function Backups() {
  const { t } = useLanguage();
  const [backups, setBackups] = useState<Backup[]>([]);
  const [selectedBackupId, setSelectedBackupId] = useState<number | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchBackups();
  }, []);

  const fetchBackups = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/backups');
      setBackups(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching backups:', err);
      setError(t('Failed to load backups'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString(undefined, {
      dateStyle: 'long',
      timeStyle: 'short',
    });
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const handleCreateBackup = async (databaseType: 'sqlite' | 'postgres') => {
    setIsCreating(true);
    try {
      const response = await adminApi.post('/backups', {
        database_type: databaseType,
        created_by: 'Admin User'
      });

      if (response.data.success) {
        alert(t("Backup created successfully!"));
        await fetchBackups();
      }
    } catch (err) {
      console.error('Error creating backup:', err);
      alert(t("Failed to create backup"));
    } finally {
      setIsCreating(false);
    }
  };

  const handleRestore = async () => {
    if (!selectedBackupId) return;

    const confirmation = window.confirm(
      t("Are you sure you want to restore this backup? All current data will be overwritten!")
    );

    if (confirmation) {
      setIsRestoring(true);
      try {
        const response = await adminApi.post(
          `/backups/${selectedBackupId}/restore`
        );

        if (response.data.success) {
          alert(t("Database successfully restored!"));
          setSelectedBackupId(null);
        }
      } catch (err) {
        console.error('Error restoring backup:', err);
        alert(t("Failed to restore backup"));
      } finally {
        setIsRestoring(false);
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t("Are you sure you want to delete this backup?"))) return;

    try {
      await adminApi.delete(`/backups/${id}`);
      alert(t("Backup deleted successfully!"));
      await fetchBackups();
    } catch (err) {
      console.error('Error deleting backup:', err);
      alert(t("Failed to delete backup"));
    }
  };

  const handleDownload = async (id: number, date: string, type: string) => {
    try {
      const response = await adminApi.get(`/backups/${id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const fileName = `backup_${type}_${date.replace(/[: ]/g, '_')}.${type === 'sqlite' ? 'db' : 'sql'}`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error downloading backup:', err);
      alert(t("Failed to download backup"));
    }
  };

  const selectedBackup = backups.find(b => b.id === selectedBackupId);

  if (loading) {
    return <div className="loading">{t("Ładowanie...")}</div>;
  }

  return (
    <div className="backups-container">
      <div className="backups-header">
        <div>
          <h2 className="backups-title">{t("Backups")}</h2>
          <p className="backups-subtitle">
            {t("Create, restore, and manage database backups")}
          </p>
        </div>
        <div className="backup-actions">
          <button
            className="btn btn-primary"
            onClick={() => handleCreateBackup('sqlite')}
            disabled={isCreating}
          >
            {isCreating ? <><Spinner /> {t("Creating...")}</> : t("Backup SQLite")}
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => handleCreateBackup('postgres')}
            disabled={isCreating}
          >
            {isCreating ? <><Spinner /> {t("Creating...")}</> : t("Backup PostgreSQL")}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">{error}</div>
      )}

      <div className="backup-list-container">
        {backups.length === 0 ? (
          <div className="empty-state">
            <p>{t("No backups found. Create your first backup using the buttons above.")}</p>
          </div>
        ) : (
          <ul className="backup-list">
            {backups.map((backup) => (
              <li
                key={backup.id}
                className={`backup-item ${selectedBackupId === backup.id ? 'selected' : ''}`}
                onClick={() => setSelectedBackupId(backup.id)}
              >
                <div className="backup-item-icon">
                  <ClockIcon />
                </div>
                <div className="backup-item-details">
                  <div className="backup-header-row">
                    <span className="backup-date">{formatDate(backup.created_at)}</span>
                    <span className={`backup-type ${backup.database_type}`}>
                      {backup.database_type.toUpperCase()}
                    </span>
                  </div>
                  <span className="backup-meta">
                    {t("Size")}: {formatSize(backup.size_bytes)} • {t("Created by")}: {backup.created_by}
                  </span>
                </div>
                <div className="backup-item-actions" style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="btn-download-backup"
                    style={{ background: '#4CAF50', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(backup.id, backup.created_at, backup.database_type);
                    }}
                  >
                    {t("Pobierz")}
                  </button>
                  <button
                    className="btn-delete-backup"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(backup.id);
                    }}
                  >
                    {t("Usuń")}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="backups-footer">
        <div className="footer-info">
          {selectedBackup ?
            `${t("Selected")}: ${formatDate(selectedBackup.created_at)} (${selectedBackup.database_type.toUpperCase()})` :
            t('Please select a backup from the list to restore.')
          }
        </div>
        <button
          className="btn btn-danger"
          onClick={handleRestore}
          disabled={!selectedBackupId || isRestoring}
        >
          {isRestoring ? (
            <>
              <Spinner /> {t("Restoring...")}
            </>
          ) : (
            t('Restore Selected Backup')
          )}
        </button>
      </div>
    </div>
  );
}

export default Backups;